import { memo, useState } from 'react';
import { Activity } from 'lucide-react';
import { Tooltip, KPI_TOOLTIPS } from '@/components/ui';
import type { TrendDataPoint } from '@/utils/analytics';

interface ActivityTrendsChartProps {
  data: TrendDataPoint[];
  isLoading?: boolean;
}

type MetricKey = 'newLeads' | 'stageChanges' | 'dealsCreated';

const METRICS: { key: MetricKey; label: string; color: string }[] = [
  { key: 'newLeads', label: 'New Leads', color: '#3B82F6' },
  { key: 'stageChanges', label: 'Stage Changes', color: '#F78C4E' },
  { key: 'dealsCreated', label: 'Deals', color: '#10B981' },
];

export const ActivityTrendsChart = memo(function ActivityTrendsChart({ data, isLoading }: ActivityTrendsChartProps) {
  const [activeMetrics, setActiveMetrics] = useState<Set<MetricKey>>(
    new Set(['newLeads', 'stageChanges', 'dealsCreated'])
  );

  const toggleMetric = (key: MetricKey) => {
    setActiveMetrics((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size > 1) next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Find max value for scaling
  const maxValue = Math.max(
    ...data.flatMap((d) =>
      METRICS.filter((m) => activeMetrics.has(m.key)).map((m) => d[m.key])
    ),
    1
  );

  // Generate SVG path for a metric
  const generatePath = (key: MetricKey): string => {
    if (data.length === 0) return '';

    const width = 100;
    const height = 60;
    const padding = 2;

    const points = data.map((d, i) => {
      const x = padding + (i / (data.length - 1 || 1)) * (width - padding * 2);
      const y = height - padding - (d[key] / maxValue) * (height - padding * 2);
      return `${x},${y}`;
    });

    return `M ${points.join(' L ')}`;
  };

  // Calculate totals for the period
  const totals = METRICS.reduce((acc, m) => {
    acc[m.key] = data.reduce((sum, d) => sum + d[m.key], 0);
    return acc;
  }, {} as Record<MetricKey, number>);

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-6">
      <div className="flex items-center gap-2 mb-4">
        <Activity className="h-5 w-5 text-emerald-400" />
        <Tooltip {...KPI_TOOLTIPS.activityTrends}>
          <h3 className="text-lg font-semibold text-white">Activity Trends</h3>
        </Tooltip>
      </div>
      <p className="text-xs text-zinc-500 mb-4">Last {data.length} days activity</p>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-600 border-t-emerald-400" />
        </div>
      ) : data.length === 0 ? (
        <p className="text-center text-zinc-500 py-4">No data available</p>
      ) : (
        <>
          {/* Legend / Toggles */}
          <div className="flex flex-wrap gap-4 mb-4">
            {METRICS.map((m) => (
              <button
                key={m.key}
                onClick={() => toggleMetric(m.key)}
                className={`flex items-center gap-2 text-xs transition-opacity ${
                  activeMetrics.has(m.key) ? 'opacity-100' : 'opacity-40'
                }`}
              >
                <span
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: m.color }}
                />
                <span className="text-zinc-300">{m.label}</span>
                <span className="font-semibold" style={{ color: m.color }}>
                  {totals[m.key]}
                </span>
              </button>
            ))}
          </div>

          {/* SVG Chart */}
          <div className="relative h-24 w-full">
            <svg
              viewBox="0 0 100 60"
              className="w-full h-full"
              preserveAspectRatio="none"
            >
              {/* Grid lines */}
              <line x1="0" y1="15" x2="100" y2="15" stroke="#27272a" strokeWidth="0.5" />
              <line x1="0" y1="30" x2="100" y2="30" stroke="#27272a" strokeWidth="0.5" />
              <line x1="0" y1="45" x2="100" y2="45" stroke="#27272a" strokeWidth="0.5" />

              {/* Lines */}
              {METRICS.filter((m) => activeMetrics.has(m.key)).map((m) => (
                <path
                  key={m.key}
                  d={generatePath(m.key)}
                  fill="none"
                  stroke={m.color}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ))}
            </svg>

            {/* Y-axis labels */}
            <div className="absolute right-0 top-0 bottom-0 flex flex-col justify-between text-[10px] text-zinc-500">
              <span>{maxValue}</span>
              <span>{Math.round(maxValue / 2)}</span>
              <span>0</span>
            </div>
          </div>

          {/* X-axis labels */}
          <div className="flex justify-between mt-1 text-[10px] text-zinc-500">
            <span>{data[0]?.date?.slice(5) ?? ''}</span>
            <span>{data[Math.floor(data.length / 2)]?.date?.slice(5) ?? ''}</span>
            <span>{data[data.length - 1]?.date?.slice(5) ?? ''}</span>
          </div>
        </>
      )}
    </div>
  );
});
